import { useCallback, useState } from 'react';

/** A site change waiting for the customer's answer, and how to put things back if they say no. */
interface PendingSiteChange {
    run: () => void;
    abandon?: () => void;
}

/**
 * What is at stake when the site changes. The containers of an order refer to the site they stand
 * at, so leaving it invalidates them.
 */
export interface SiteChangeGuard {
    /** How many positions would be lost by changing the site. */
    positionCount: number;
    /** Drops them, once the customer has confirmed. */
    discard: () => void;
}

/**
 * Runs a site change, asking first while positions hang off the current site. The confirmed change
 * drops them, since they name containers at a site the order no longer refers to.
 */
// @ai-generated 2026-10-26T21:47:14+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function useSiteChangeGuard(siteChangeGuard: SiteChangeGuard | undefined) {
    const [pendingSiteChange, setPendingSiteChange] = useState<PendingSiteChange | undefined>(undefined);

    const guardSiteChange = useCallback(
        (change: () => void, abandon?: () => void) => {
            if (!siteChangeGuard || siteChangeGuard.positionCount === 0) {
                change();
                return;
            }
            setPendingSiteChange({ run: change, abandon });
        },
        [siteChangeGuard],
    );

    const confirmSiteChange = useCallback(() => {
        siteChangeGuard?.discard();
        pendingSiteChange?.run();
        setPendingSiteChange(undefined);
    }, [siteChangeGuard, pendingSiteChange]);

    const cancelSiteChange = useCallback(() => {
        pendingSiteChange?.abandon?.();
        setPendingSiteChange(undefined);
    }, [pendingSiteChange]);

    return { guardSiteChange, confirmSiteChange, cancelSiteChange, isAsking: pendingSiteChange !== undefined };
}
